import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import MapView from "./components/MapView/MapView";
import Sidebar from "./components/Sidebar/Sidebar";
import Header from "./components/Header/Header";
import AboutUs from "./components/AboutUs/AboutUs";
import Methodology from "./components/Methodology/Methodology";
import Stories from "./components/Stories/Stories";
import FilterDrawer from "./components/FilterDrawer/FilterDrawer";
import rawMarkers from "./data/facilities_markers.json";
import { useFilters } from "./hooks/useFilters";
import OnboardingTour from "./components/OnboardingTour/OnboardingTour";
import { posthog } from "./config/posthog";
import { parseCurrentUrl, getFacilityUrl, getViewUrl, updateBrowserUrl } from "./utils/urlHelper";

function App() {
  // Read initial route from window location on app load
  const initialUrlState = useRef(parseCurrentUrl());
  const [activeView, setActiveView] = useState(() => initialUrlState.current.view);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [flyToTarget, setFlyToTarget] = useState(() => {
    const { facilityId } = initialUrlState.current;
    if (facilityId) {
      const match = rawMarkers.find((m) => m.id === facilityId || m.os_id === facilityId);
      return match || { id: facilityId, name: 'Facility' };
    }
    return null;
  });

  // Filter hook
  const filters = useFilters(rawMarkers);

  // Sync state to URL when selectedMarker or activeView changes
  useEffect(() => {
    if (selectedMarker && selectedMarker.id) {
      const targetUrl = getFacilityUrl(selectedMarker.id);
      updateBrowserUrl(targetUrl);
    } else if (activeView) {
      const targetUrl = getViewUrl(activeView);
      updateBrowserUrl(targetUrl);
    }
  }, [selectedMarker, activeView]);

  // Handle browser back/forward navigation (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const { view, facilityId } = parseCurrentUrl();
      if (facilityId) {
        if (!selectedMarker || (selectedMarker.id !== facilityId && selectedMarker.os_id !== facilityId)) {
          const match = rawMarkers.find((m) => m.id === facilityId || m.os_id === facilityId);
          setFlyToTarget(match || { id: facilityId, name: 'Facility' });
        }
        if (activeView !== "markers") {
          setActiveView("markers");
        }
      } else {
        if (selectedMarker) {
          setSelectedMarker(null);
        }
        if (activeView !== view) {
          setActiveView(view);
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectedMarker, activeView]);

  // Track active view changes
  useEffect(() => {
    posthog.capture("view_changed", {
      view: activeView,
    });
  }, [activeView]);

  // Track when a facility marker is selected and capture page view
  useEffect(() => {
    if (selectedMarker && selectedMarker.id) {
      const currentUrl = window.location.href;
      posthog.capture("facility_selected", {
        facility_id: selectedMarker.id,
        facility_name: selectedMarker.name,
        city: selectedMarker.city,
        state: selectedMarker.state,
        brands: selectedMarker.associatedCompanies || [],
        organisation: selectedMarker.organisation,
        url: currentUrl,
      });
      posthog.capture("$pageview", {
        $current_url: currentUrl,
      });
    }
  }, [selectedMarker]);

  // Track when filters are applied
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    posthog.capture("filters_applied", {
      states: filters.appliedStates,
      organisations: filters.appliedOrganisations,
      has_active_filters: filters.hasActiveFilters,
    });
  }, [filters.appliedStates, filters.appliedOrganisations, filters.hasActiveFilters]);

  // Handle facility selection from search
  const handleSelectFacility = (facility) => {
    // Switch to map view if not already there
    if (activeView !== "markers") {
      setActiveView("markers");
    }

    // Set the facility to fly to - MapView will handle this
    setFlyToTarget(facility);

    // Track search selection
    posthog.capture("facility_searched", {
      facility_id: facility.id,
      facility_name: facility.name,
      state: facility.state,
    });
  };

  return (
    <div className="app-container">
      {/* ✅ Header with view control and search */}
      <Header
        activeView={activeView}
        onChangeView={setActiveView}
        onSelectFacility={handleSelectFacility}
        onToggleFilterDrawer={filters.openDrawer}
        hasActiveFilters={filters.hasActiveFilters}
      />

      <div className="content">
        {/* ✅ Conditionally render views */}
        {activeView === "markers" && (
          <>
            <MapView
              selectedMarker={selectedMarker}
              setSelectedMarker={setSelectedMarker}
              flyToTarget={flyToTarget}
              setFlyToTarget={setFlyToTarget}
              filteredMarkers={filters.filteredMarkers}
              appliedStates={filters.appliedStates}
              appliedOrganisations={filters.appliedOrganisations}
            />
            {selectedMarker && (
              <Sidebar
                selectedMarker={selectedMarker}
                setSelectedMarker={setSelectedMarker}
              />
            )}
          </>
        )}

        {activeView === "about" && <AboutUs />}
        {activeView === "methodology" && <Methodology />}
        {activeView === "stories" && <Stories />}
      </div>

      {/* Filter drawer */}
      <FilterDrawer
        isOpen={filters.isFilterDrawerOpen}
        onClose={filters.closeDrawer}
        states={filters.states}
        organisations={filters.organisations}
        selectedStates={filters.selectedStates}
        selectedOrganisations={filters.selectedOrganisations}
        appliedStates={filters.appliedStates}
        appliedOrganisations={filters.appliedOrganisations}
        onToggleState={filters.toggleState}
        onToggleOrganisation={filters.toggleOrganisation}
        onApply={filters.applyFilters}
        onClear={filters.clearFilters}
        isLoading={filters.isLoading}
        isApplying={filters.isApplying}
        error={filters.error}
        noResults={filters.noResults}
      />
      <OnboardingTour />
    </div >
  );
}

export default App;
